import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Package, CheckCircle, Clock, ChevronRight, Loader2 } from "lucide-react";
import { API } from "../App";

const POLL_INTERVAL_MS = 30000;

const getIcon = (type) => {
  switch (type) {
    case "order_placed":
      return <Package size={16} className="text-green-600" />;
    case "order_status":
      return <CheckCircle size={16} className="text-blue-600" />;
    case "broadcast":
      return <Bell size={16} className="text-amber-500" />;
    default:
      return <Bell size={16} className="text-gray-500" />;
  }
};

const formatTime = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export default function NotificationsPane() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API}/notifications`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      // silent; sidebar shouldn't crash
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await fetch(`${API}/notifications/mark-read`, { method: "PUT", credentials: "include" });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (e) {
      // silent
    }
  };

  const unread = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900" data-testid="notifications-pane">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/85 dark:bg-gray-900/85 backdrop-blur border-b border-gray-100 dark:border-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-green-50 dark:bg-emerald-900/40 flex items-center justify-center">
              <Bell size={16} className="text-green-700 dark:text-emerald-300" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                Notifications
              </h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {unread > 0 ? `${unread} unread` : "All caught up"}
              </p>
            </div>
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-[11px] font-semibold text-green-700 hover:text-green-800 dark:text-emerald-300 dark:hover:text-emerald-200 transition-colors"
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Loader2 size={20} className="animate-spin mb-2" />
            <p className="text-xs">Loading notifications…</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 flex items-center justify-center">
              <Bell size={22} className="text-gray-300 dark:text-gray-600" />
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              No notifications yet
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
              Order updates and offers will appear here.
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="notifications-list">
            {notifications.map((notif) => (
              <li key={notif.notification_id}>
                <button
                  type="button"
                  onClick={() => {
                    if (notif.url) {
                      if (notif.url.startsWith("http")) {
                        window.open(notif.url, "_blank");
                      } else {
                        navigate(notif.url);
                      }
                    } else if (notif.order_id) {
                      navigate(`/orders/${notif.order_id}`);
                    }
                  }}
                  className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                    notif.is_read
                      ? "bg-white dark:bg-gray-800/60 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                      : "bg-green-50/70 dark:bg-emerald-900/20 border-green-100 dark:border-emerald-800/40 hover:bg-green-50 dark:hover:bg-emerald-900/30"
                  }`}
                  data-testid={`notification-pane-item-${notif.notification_id}`}
                >
                  <div className="mt-0.5 flex-shrink-0">{getIcon(notif.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                      {notif.title}
                    </p>
                    {notif.message && (
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                        {notif.message}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 flex items-center gap-1">
                      <Clock size={10} /> {formatTime(notif.created_at)}
                    </p>
                  </div>
                  {(notif.order_id || notif.url) && (
                    <ChevronRight size={14} className="text-gray-300 dark:text-gray-600 mt-1 flex-shrink-0" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
